import { getMessageWithPlaceholders } from 'shared-locales';

import type { GetEventsRes } from 'shared-types/responses';
import type { ReduxRootState } from 'types';

interface Params {
  event: NonNullable<GetEventsRes['events']>[0];
  messages: ReduxRootState['messages'];
  includeCalendarEvent?: boolean;
  includeCourse?: boolean;
  includePath?: boolean;
}

const getEventMessage = ({
  event,
  messages,
  includeCalendarEvent = false,
  includeCourse = false,
  includePath = false
}: Params) => {
  const activated = messages.activitiesTextActivated;
  const added = messages.activitiesTextAdded;
  const changed = messages.activitiesTextChanged;
  const completed = messages.activitiesTextCompleted;
  const copied = messages.activitiesTextCopied;
  const created = messages.activitiesTextCreated;
  const deleted = messages.activitiesTextDeleted;
  const denied = messages.activitiesTextDenied;
  const disabled = messages.activitiesTextDisabled;
  const discarded = messages.activitiesTextDiscarded;
  const published = messages.activitiesTextPublished;
  const removed = messages.activitiesTextRemoved;
  const savedChanges = messages.activitiesTextSavedChanges;
  const sent = messages.activitiesTextSent;
  const set = messages.activitiesTextSet;
  const unpublished = messages.activitiesTextUnpublished;
  const updated = messages.activitiesTextUpdated;

  const getEventCourse = () => {
    return event.eventCourse && event.eventCourse.title
      ? event.eventCourse.title
      : messages.activitiesTextUnknownCourse;
  };

  const getCompetency = () => {
    return event.competency && event.competency.name
      ? event.competency.name
      : messages.activitiesTextUnknownCompetency;
  };

  const getCompetencyProfile = () => {
    return event.competencyProfile?.title
      ? event.competencyProfile.title
      : messages.activitiesTextUnknownCompetencyProfile;
  };

  const getCourse = () => {
    return event.course && event.course.title
      ? event.course.title
      : messages.activitiesTextUnknownCourse;
  };

  const getUser = () => {
    return event.user && (event.user.email || event.user.username)
      ? event.user.email
        ? event.user.email
        : event.user.username
      : messages.activitiesTextUnknownUser;
  };

  const getUserFromData = () => {
    return (
      event.data?.user?.email ||
      event.data?.user?.username ||
      messages.activitiesTextUnknownUser
    );
  };

  const getAdminGroup = () => {
    return event.adminGroup && event.adminGroup.title
      ? event.adminGroup.title
      : messages.activitiesTextUnknownAdminGroup;
  };

  const getCalendarEvent = () => {
    return event.calendarEvent && event.calendarEvent.title
      ? event.calendarEvent.title
      : event.data?.calendarEventTitle
      ? event.data.calendarEventTitle
      : messages.activitiesTextUnknownCalendarEvent;
  };

  const getChapter = () => {
    return event.chapter && event.chapter.title
      ? event.chapter.title
      : messages.activitiesTextUnknownChapter;
  };

  const getChapterVersion = () => {
    return event.data && event.data.chapterVersion
      ? event.data.chapterVersion
      : messages.activitiesTextUnknownChapterVersion;
  };

  const getClientPageSection = () => {
    return event.clientPageSection && event.clientPageSection.title
      ? event.clientPageSection.title
      : messages.activitiesTextUnknownClientPageSection;
  };

  const getCourseVersion = () => {
    return event.data && event.data.courseVersion
      ? event.data.courseVersion
      : messages.activitiesTextUnknownCourseVersion;
  };

  const getDeletedAchievement = () => {
    return event.data && event.data.achievementTitle
      ? event.data.achievementTitle
      : messages.activitiesTextUnknownAchievement;
  };

  const getDeletedAdminGroup = () => {
    return event.data && event.data.adminGroupTitle
      ? event.data.adminGroupTitle
      : messages.activitiesTextUnknownAdminGroup;
  };

  const getDeletedAuthStrategy = () => {
    return event.data?.identifier || 'unknown strategy';
  };

  const getDeletedCompetency = () => {
    return event.data && event.data.competencyName
      ? event.data.competencyName
      : messages.activitiesTextUnknownCompetency;
  };

  const getDeletedCompetencyProfile = () => {
    return event.data && event.data.competencyProfileTitle
      ? event.data.competencyProfileTitle
      : messages.activitiesTextUnknownCompetencyProfile;
  };

  const getDeletedCourse = () => {
    return event.data && event.data.courseTitle
      ? event.data.courseTitle
      : messages.activitiesTextUnknownCourse;
  };

  const getDeletedChapter = () => {
    return event.data && event.data.chapterTitle
      ? event.data.chapterTitle
      : messages.activitiesTextUnknownChapter;
  };

  const getDeletedClientPageSection = () => {
    return event.data && event.data.sectionTitle
      ? event.data.sectionTitle
      : messages.activitiesTextUnknownClientPageSection;
  };

  const getDeletedClientPageSectionAttachment = () => {
    return event.data && event.data.attachmentName
      ? event.data.attachmentName
      : messages.activitiesTextUnknownAttachment;
  };

  const getDeletedGroup = () => {
    return event.data && event.data.groupTitle
      ? event.data.groupTitle
      : messages.activitiesTextUnknownGroup;
  };

  const getDeletedIndividualAchievement = () => {
    return event.data && event.data.individualAchievementTitle
      ? event.data.individualAchievementTitle
      : messages.activitiesTextUnknownAchievement;
  };

  const getDeletedIntegration = () => {
    return event.data?.identifier || event.data?.title || 'unknown integration';
  };

  const getDeletedPath = () => {
    return event.data && event.data.pathTitle
      ? event.data.pathTitle
      : messages.activitiesTextUnknownPath;
  };

  const getDeletedProductUserCustomAttribute = () => {
    return event.data && event.data.productUserCustomAttributeTitle
      ? event.data.productUserCustomAttributeTitle
      : messages.activitiesTextUnknownProductUserCustomAttribute;
  };

  const getDeletedUser = () => {
    return event.data && (event.data.email || event.data.username)
      ? event.data.email
        ? event.data.email
        : event.data.username
      : messages.activitiesTextUnknownUser;
  };

  const getEmailMessageTemplate = () => {
    return event.emailMessageTemplate && event.emailMessageTemplate.title
      ? event.emailMessageTemplate.title
      : messages.activitiesTextUnknownTemplate;
  };

  const getGroup = () => {
    return event.group && event.group.title
      ? event.group.title
      : messages.activitiesTextUnknownGroup;
  };

  const getIndividualAchievement = () => {
    return event.individualAchievement && event.individualAchievement.title
      ? event.individualAchievement.title
      : messages.activitiesTextUnknownAchievement;
  };

  const getIntegrationIdentifier = () => {
    return (
      event.integration?.identifier ||
      event.integration?.title ||
      'unknown integration'
    );
  };

  const getPath = () => {
    return event.path && event.path.title
      ? event.path.title
      : messages.activitiesTextUnknownPath;
  };

  const getPathStep = () => {
    return event.pathStep && event.pathStep.number
      ? event.pathStep.number
      : messages.commonUnknown.toLowerCase();
  };

  const getProductUserCustomAttribute = () => {
    return event.productUserCustomAttribute &&
      event.productUserCustomAttribute.title
      ? event.productUserCustomAttribute.title
      : messages.activitiesTextUnknownProductUserCustomAttribute;
  };

  const getAchievement = () => {
    return event.achievement && event.achievement.title
      ? event.achievement.title
      : messages.activitiesTextUnknownAchievement;
  };

  const calendarEvent = () => {
    return includeCalendarEvent ? ` ${getCalendarEvent()}` : '';
  };

  const fromCalendarEvent = () =>
    includeCalendarEvent
      ? ` ${messages.activitiesTextFromCalendarEvent} ${getCalendarEvent()}`
      : '';

  const inCalendarEvent = () =>
    includeCalendarEvent
      ? ` ${messages.activitiesTextInCalendarEvent} ${getCalendarEvent()}`
      : '';

  const toCalendarEvent = () =>
    includeCalendarEvent
      ? ` ${messages.activitiesTextToCalendarEvent} ${getCalendarEvent()}`
      : '';

  const course = () => {
    return includeCourse ? ` ${getEventCourse()}` : '';
  };

  const inCourse = () => {
    return includeCourse
      ? ` ${messages.activitiesTextInCourse} ${getEventCourse()}`
      : '';
  };

  const inCompetency = () =>
    ` ${messages.activitiesTextInCompetency} ${getCompetency()}`;

  const inCompetencyProfile = () =>
    ` ${messages.activitiesTextInCompetencyProfile} ${getCompetencyProfile()}`;

  const inChapter = () => `${messages.activitiesTextInChapter} ${getChapter()}`;

  const fromGroup = () => ` ${messages.activitiesTextFromGroup} ${getGroup()}`;

  const inGroup = () => ` ${messages.activitiesTextInGroup} ${getGroup()}`;

  const toGroup = () => ` ${messages.activitiesTextToGroup} ${getGroup()}`;

  const path = () => {
    return includePath ? ` ${getPath()}` : '';
  };

  const fromPath = () =>
    includePath ? ` ${messages.activitiesTextFromPath} ${getPath()}` : '';

  const inPath = () =>
    includePath ? ` ${messages.activitiesTextInPath} ${getPath()}` : '';

  const toPath = () =>
    includePath ? ` ${messages.activitiesTextToPath} ${getPath()}` : '';

  const inAchievement = () =>
    ` ${messages.activitiesTextInAchievement} ${getAchievement()}`;

  switch (event.type) {
    case 'accept-individual-achievement-request':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAcceptIndividualAchievement,
        placeholders: {
          individualAchievement: getIndividualAchievement()
        }
      });
    case 'activate-two-factor-authentication':
      return `${activated} ${messages.authTwoFactorAuthentication?.toLowerCase()}`;
    case 'activate-shared-two-factor-authentication':
      return `${activated} ${messages.authTwoFactorAuthenticationShared?.toLowerCase()}`;
    case 'achievement-access-denied':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAchievementAccessDenied,
        placeholders: {
          achievementTitle: getAchievement(),
          numberOfUsers: event.data?.deniedUserIds?.length
        }
      });
    case 'add-achievement-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddAdminPermissionsGroup
            : messages.activitiesCommonAddAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inAchievement()
      );
    case 'add-achievement-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddOwnerRightsGroup
            : messages.activitiesCommonAddOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inAchievement()
      );
    case 'add-achievement-completions':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddAchievementUsers,
        placeholders: {
          achievement: getAchievement(),
          numberOfUsers:
            event.data?.addedUserIds?.length || event.data?.members?.length
        }
      });
    case 'add-admin-group-user':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddAdminGroupUser,
        placeholders: {
          numberOfUsers: event.data?.addedUserIds?.length,
          adminGroup: getAdminGroup()
        }
      });
    case 'add-calendar-event-attachment':
      return (
        messages.activitiesMessageAddCalendarEventAttachment + toCalendarEvent()
      );
    case 'add-calendar-event-feedback-question':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageAddCalendarEventFeedbackQuestion,
          placeholders: { user: '' }
        }) + inCalendarEvent()
      );
    case 'add-calendar-event-feedback-template':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddCalendarEventFeedbackTemplate,
        placeholders: {
          templateTitle: event.data?.templateTitle,
          user: ''
        }
      });
    case 'add-calendar-event-feedback-template-question':
      return getMessageWithPlaceholders({
        message:
          messages.activitiesMessageAddCalendarEventFeedbackTemplateQuestion,
        placeholders: {
          templateTitle: event.data?.templateTitle,
          user: ''
        }
      });
    case 'apply-calendar-event-feedback-template':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageApplyCalendarEventFeedbackTemplate,
          placeholders: {
            templateTitle: event.data?.templateTitle,
            user: ''
          }
        }) + inCalendarEvent()
      );
    case 'add-calendar-event-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddOwnerRightsGroup
            : messages.activitiesCommonAddOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCalendarEvent()
      );
    case 'add-calendar-event-participation':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageAddCalendarEventParticipation,
          placeholders: {
            numberOfUsers: event.data?.userIds?.length || 1
          }
        }) + inCalendarEvent()
      );
    case 'add-calendar-event-self':
      return messages.activitiesMessageAddCalendarEventSelf + calendarEvent();
    case 'add-calendar-event-users':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageAddCalendarEventUsers,
          placeholders: {
            numberOfUsers: event.data?.addedUserIds?.length
          }
        }) + toCalendarEvent()
      );
    case 'add-client-page-section':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddClientPageSection,
        placeholders: { sectionTitle: getClientPageSection() }
      });
    case 'add-client-page-section-attachment':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddClientPageSectionAttachment,
        placeholders: { sectionTitle: getClientPageSection() }
      });
    case 'add-competency-endorsement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddCompetencyEndorsement,
        placeholders: {
          user: getUser(),
          competency: getCompetency()
        }
      });
    case 'add-competency-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddAdminPermissionsGroup
            : messages.activitiesCommonAddAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCompetency()
      );
    case 'add-competency-profile-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddAdminPermissionsGroup
            : messages.activitiesCommonAddAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCompetencyProfile()
      );
    case 'add-course-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddAdminPermissionsGroup
            : messages.activitiesCommonAddAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCourse()
      );
    case 'add-course-language-version':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageAddCourseLanguageVersion,
          placeholders: {
            language: event.data?.language,
            title: event.data?.title,
            user: ''
          }
        }) + inCourse()
      );
    case 'add-course-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddOwnerRightsGroup
            : messages.activitiesCommonAddOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCourse()
      );
    case 'add-course-users':
      return (
        `${added} ` +
        (event.data && event.data.addedUserIds
          ? `${event.data.addedUserIds.length} `
          : '') +
        `${messages.activitiesTextUsers} ${
          messages.activitiesTextToCourse
        }${course()}`
      );
    case 'add-email-message-template-product':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddEmailMessageTemplateProduct,
        placeholders: {
          resourceTitle: event.data.productTitle,
          templateTitle: getEmailMessageTemplate(),
          user: ''
        }
      });
    case 'add-group-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddAdminPermissionsGroup
            : messages.activitiesCommonAddAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inGroup()
      );
    case 'add-group-course':
      return `${added} ${
        messages.activitiesTextCourse
      } ${getCourse()}${toGroup()}`;
    case 'add-group-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddOwnerRightsGroup
            : messages.activitiesCommonAddOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inGroup()
      );
    case 'add-group-to-supervisor':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddGroupToSupervisor,
        placeholders: {
          groupTitle: getGroup(),
          user: getUser()
        }
      });
    case 'add-group-users':
      return (
        `${added} ` +
        (event.data && event.data.addedUserIds
          ? `${event.data.addedUserIds.length} `
          : '') +
        `${messages.activitiesTextUsers}${toGroup()}`
      );
    case 'add-path-group':
      return `${added} ${
        messages.activitiesTextGroup
      } ${getGroup()}${toPath()}`;
    case 'add-path-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonAddOwnerRightsGroup
            : messages.activitiesCommonAddOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inPath()
      );
    case 'add-path-step':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageAddPathStep,
          placeholders: { stepNumber: getPathStep() }
        }) + toPath()
      );
    case 'add-path-users':
      return (
        `${added} ` +
        (event.data && event.data.addedUserIds
          ? `${event.data.addedUserIds.length} `
          : '') +
        `${messages.activitiesTextUsers}${toPath()}`
      );
    case 'add-supervisees':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddNewSupervisees,
        placeholders: { user: getUser() }
      });
    case 'add-user-competencies':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAddUserCompetencies,
        placeholders: {
          addedCompetencies: event.data?.addedCompetencies?.join(', '),
          user: getUser()
        }
      });
    case 'allow-new-course-completion-attempt':
      return `${messages.activitiesTextAllowedRetry} ${getUser()}` + inCourse();
    case 'calendar-event-access-denied':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesCommonAccessRequestDenied,
          placeholders: {
            numberOfUsers: event.data?.deniedUserIds?.length
          }
        }) + toCalendarEvent()
      );
    case 'change-supervisor':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageChangeSupervisor,
        placeholders: {
          previousUser:
            event?.data?.oldSupervisor || messages.activitiesTextUnknownUser,
          newUser: getUser()
        }
      });
    case 'competency-access-denied':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageCompetencyAccessDenied,
          placeholders: { user: getUser() }
        }) + inCompetency()
      );
    case 'complete-course':
      return `${completed} ${messages.activitiesTextCourse}${course()}`;
    case 'complete-course-failed':
      return `${messages.activitiesTextCompletionFailed} ${inCourse()}`;
    case 'complete-course-waiting':
      return `${messages.activitiesTextCompletionWaiting}${inCourse()}`;
    case 'complete-path':
      return `${completed} ${messages.activitiesTextPath}${path()}`;
    case 'copy-calendar-event':
      return messages.activitiesMessageCopiedCalendarEvent + calendarEvent();
    case 'copy-chapter':
      return (
        `${copied} ${messages.activitiesTextChapter}` +
        ` ${getChapter()}${inCourse()}`
      );
    case 'copy-competency':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCopyCompetency,
        placeholders: {
          competency: getCompetency(),
          sourceCompetency:
            event.data?.sourceCompetencyName ||
            messages.activitiesTextUnknownCompetency
        }
      });
    case 'copy-course':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCopiedCourse,
        placeholders: {
          course: includeCourse ? getEventCourse() : '',
          user: ''
        }
      });
    case 'copy-path':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCopiedPath,
        placeholders: { path: includePath ? getPath() : '' }
      });
    case 'course-access-denied':
      return (
        `${denied} ` +
        (event.data && event.data.deniedUserIds
          ? `${event.data.deniedUserIds.length} `
          : '') +
        `${messages.activitiesTextUsers} ${messages.activitiesTextAccess} ${
          messages.activitiesTextToCourse
        }${course()}`
      );
    case 'create-achievement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCreateAchievement,
        placeholders: {
          achievement: getAchievement()
        }
      });
    case 'create-achievement-product-user-custom-attribute':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageCreateProductUserCustomAttribute,
          placeholders: {
            productUserCustomAttribute: getProductUserCustomAttribute()
          }
        }) + inAchievement()
      );
    case 'create-admin-group':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCreateAdminGroup,
        placeholders: {
          adminGroup: getAdminGroup()
        }
      });
    case 'create-answer':
      return `${created} ${messages.activitiesTextAnswer}${inCourse()}`;
    case 'create-answer-review':
      return `${created} ${messages.activitiesTextAnswerReview}${inCourse()}`;
    case 'create-auth-strategy':
      return `created auth strategy ${event.data?.identifier}`;
    case 'create-calendar-event':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCreateCalendarEvent,
        placeholders: {
          calendarEvent: getCalendarEvent()
        }
      });
    case 'create-calendar-event-product-user-custom-attribute':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageCreateProductUserCustomAttribute,
          placeholders: {
            productUserCustomAttribute: getProductUserCustomAttribute()
          }
        }) + inCalendarEvent()
      );
    case 'create-chapter':
      return `${created} ${
        messages.activitiesTextChapter
      } ${getChapter()}${inCourse()}`;
    case 'create-chat-message':
      return `${created} ${messages.activitiesTextChatMessage}${inCourse()}`;
    case 'create-competency':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCreateCompetency,
        placeholders: { competency: getCompetency() }
      });
    case 'create-competency-profile':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCreateCompetencyProfile,
        placeholders: { competencyProfile: getCompetencyProfile() }
      });
    case 'create-course':
      return `${created} ${messages.activitiesTextCourse} ${getCourse()}`;
    case 'create-discussion-comment':
      return `${created} ${
        messages.activitiesTextDiscussionComment
      }${inCourse()}`;
    case 'create-email-message-template':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCreateEmailMessageTemplate,
        placeholders: {
          templateTitle: getEmailMessageTemplate(),
          user: ''
        }
      });
    case 'create-feedback':
      return `${created} ${messages.activitiesTextFeedback}${inCourse()}`;
    case 'create-group':
      return `${created} ${messages.activitiesTextGroup} ${getGroup()}`;
    case 'create-individual-achievement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageCreateIndividualAchievement,
        placeholders: {
          individualAchievement: getIndividualAchievement()
        }
      });
    case 'create-integration':
      return `created integration ${getIntegrationIdentifier()}`;
    case 'create-path':
      return `${created} ${messages.activitiesTextPath} ${getPath()}`;
    case 'create-h5p-status':
      return `${created} ${messages.activitiesTextH5PStatus}`;
    case 'create-scorm-status':
      return `${created} ${messages.activitiesTextScormStatus}`;
    case 'create-supervisor':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageAssignNewSupervisor,
        placeholders: { user: getUser() }
      });
    case 'create-user':
      return `${created} ${messages.activitiesTextUser} ${getUser()}`;
    case 'delete-account':
      return `${getDeletedUser()} ${deleted} ${
        messages.activitiesTextTheirAccount
      }`;
    case 'delete-achievement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteAchievement,
        placeholders: {
          achievement: getDeletedAchievement()
        }
      });
    case 'delete-admin-group':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteAdminGroup,
        placeholders: {
          adminGroup: getDeletedAdminGroup()
        }
      });
    case 'delete-all-admin-groups':
      return messages.activitiesMessageDeleteAllAdminGroups;
    case 'delete-answer':
      return `${deleted} ${messages.activitiesTextAnswer}${inCourse()}`;
    case 'delete-answer-review':
      return `${deleted} ${messages.activitiesTextAnswerReview}${inCourse()}`;
    case 'delete-auth-strategy':
      return `deleted auth strategy ${getDeletedAuthStrategy()}`;
    case 'delete-calendar-event':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteCalendarEvent,
        placeholders: { calendarEvent: getCalendarEvent() }
      });
    case 'delete-chapter':
      return (
        `${deleted} ${messages.activitiesTextChapter} ${getDeletedChapter()}` +
        inCourse()
      );
    case 'delete-chat-messages':
      return `${deleted} ${messages.activitiesTextChatMessage}${inCourse()}`;
    case 'delete-client-page-section':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteClientPageSection,
        placeholders: { sectionTitle: getDeletedClientPageSection() }
      });
    case 'delete-competency':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteCompetency,
        placeholders: {
          competency: getDeletedCompetency()
        }
      });
    case 'delete-all-competencies':
      return messages.activitiesMessageDeleteAllCompetencies;
    case 'delete-competency-endorsement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteCompetencyEndorsement,
        placeholders: {
          user: getUser(),
          competency: getCompetency()
        }
      });
    case 'delete-competency-profile':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteCompetencyProfile,
        placeholders: {
          competencyProfile: getDeletedCompetencyProfile()
        }
      });
    case 'delete-course-language-version':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageDeleteCourseLanguageVersion,
          placeholders: {
            language: event.data?.language,
            user: ''
          }
        }) + inCourse()
      );
    case 'delete-all-competency-profiles':
      return messages.activitiesMessageDeleteAllCompetencyProfiles;
    case 'delete-course':
      return `${deleted} ${
        messages.activitiesTextCourse
      } ${getDeletedCourse()}`;
    case 'delete-course-data':
      return (
        `${deleted} ${messages.activitiesTextCourseData}` +
        ` ${messages.activitiesTextFromUser} ${getUser()}`
      );
    case 'delete-discussion-comment':
      return `${deleted} ${
        messages.activitiesTextDiscussionComment
      }${inCourse()}`;
    case 'delete-email-message-template':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteEmailMessageTemplate,
        placeholders: {
          templateTitle: event.data.templateTitle,
          user: ''
        }
      });
    case 'delete-expired-user':
      return `${getDeletedUser()} ${messages.activitiesTextWasDeleted}`;
    case 'delete-feedback':
      return `${deleted} ${messages.activitiesTextFeedback}${inCourse()}`;
    case 'delete-group':
      return `${deleted} ${messages.activitiesTextGroup} ${getDeletedGroup()}`;
    case 'delete-individual-achievement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteIndividualAchievement,
        placeholders: {
          individualAchievement: getDeletedIndividualAchievement()
        }
      });
    case 'delete-integration':
      return `deleted integration ${getDeletedIntegration()}`;
    case 'delete-path':
      return `${deleted} ${messages.activitiesTextPath} ${getDeletedPath()}`;
    case 'delete-path-completion':
      return getMessageWithPlaceholders({
        message: messages.activitiesTextPathCompletion,
        placeholders: { user: getUser(), inPath: inPath() }
      });
    case 'delete-supervisor':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDeleteSupervisor,
        placeholders: { user: getUser() }
      });
    case 'delete-user':
      return `${deleted} ${messages.activitiesTextUser} ${getDeletedUser()}`;
    case 'delete-user-course-analytics':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageDeleteUserCourseAnalytics,
          placeholders: { user: getUser() }
        }) + inCourse()
      );
    case 'deny-individual-achievement-request':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageDenyIndividualAchievement,
        placeholders: {
          individualAchievement: getDeletedIndividualAchievement()
        }
      });
    case 'disable-two-factor-authentication':
      return `${disabled} ${messages.authTwoFactorAuthentication?.toLowerCase()}`;
    case 'disable-shared-two-factor-authentication':
      return `${disabled} ${messages.authTwoFactorAuthenticationShared?.toLowerCase()}`;
    case 'discard-chapter-changes':
      return `${discarded} ${getChapter()}${inCourse()}`;
    case 'external-add-course-user':
      return (
        `${getUser()} ${messages.activitiesTextWasAdded}` +
        ` ${messages.activitiesTextToCourse}${course()} ${
          messages.activitiesTextViaApi
        }`
      );
    case 'external-add-group-user':
      return (
        `${getUser()} ${messages.activitiesTextWasAdded}` +
        ` ${messages.activitiesTextToGroup}${getGroup()} ${
          messages.activitiesTextViaApi
        }`
      );
    case 'external-add-path-user':
      return (
        `${getUser()} ${messages.activitiesTextWasAdded}` +
        ` ${messages.activitiesTextToPath}${getPath()} ${
          messages.activitiesTextViaApi
        }`
      );
    case 'external-add-user':
      return (
        `${getUser()} ${messages.activitiesTextWasAdded}` +
        ` ${messages.activitiesTextViaApi}`
      );
    case 'external-update-user':
      return (
        `${messages.activitiesTextUpdated} ${getUser()}` +
        ` ${messages.activitiesTextViaApi}`
      );
    case 'external-copy-course':
      return (
        `${getCourse()} ${messages.activitiesTextWasCopied}` +
        ` ${messages.activitiesTextViaApi}`
      );
    case 'external-delete-user':
      return (
        `${getDeletedUser()} ${messages.activitiesTextWasDeleted}` +
        ` ${messages.activitiesTextViaApi}`
      );
    case 'external-mycashflow-order-event':
      return (
        `${getUser()} ${messages.activitiesTextWasAdded}` +
        (event.group
          ? toGroup()
          : ` ${messages.activitiesTextToCourse}${course()}`) +
        ` ${messages.activitiesTextViaMyCashflow}`
      );
    case 'external-shopify-order-event':
      return (
        `${getUser()} ${messages.activitiesTextWasAdded}` +
        (event.group
          ? toGroup()
          : ` ${messages.activitiesTextToCourse}${course()}`) +
        ` ${messages.activitiesTextViaShopify}`
      );
    case 'external-woocommerce-order-event':
      return (
        `${getUser()} ${messages.activitiesTextWasAdded}` +
        (event.group
          ? toGroup()
          : ` ${messages.activitiesTextToCourse}${course()}`) +
        ` ${messages.activitiesTextViaWooCommerce}`
      );
    case 'fail-background-job':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageFailBackgroundJob,
        placeholders: { jobType: event.data?.jobType || '' }
      });
    case 'generate-ai-translations':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageGenerateAITranslations,
          placeholders: {
            language: event.data?.targetLanguage,
            user: ''
          }
        }) + inCourse()
      );
    case 'generate-two-factor-backup-codes':
      return `${created} ${messages.activitiesTextNewTwoFactorBackupCodes}`;
    case 'hubspot-sync-contacts':
      return `Hubspot ${messages.integrationsSyncContacts}`;
    case 'import-achievement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageImportAchievement,
        placeholders: { achievementTitle: getAchievement(), user: '' }
      });
    case 'import-achievement-completions':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageImportAchievementCompletions,
        placeholders: { achievementTitle: getAchievement(), user: '' }
      });
    case 'import-achievement-template':
      return messages.activitiesMessageImportAchievementTemplate;
    case 'import-course-language-version-json':
      return `${getMessageWithPlaceholders({
        message: messages.activitiesMessageImportCourseLanguageVersionJson,
        placeholders: { language: event.data?.languageVersion, user: '' }
      })} ${inCourse()}`;
    case 'join-course-with-link':
      return messages.activitiesMessageJoinCourseWithLink + inCourse();
    case 'join-path-with-link':
      return messages.activitiesMessageJoinPathWithLink + inPath();
    case 'link-achievement-to-calendar-event':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageLinkAchievementToCalendarEvent,
          placeholders: {
            achievementTitle: getAchievement()
          }
        }) + toCalendarEvent()
      );
    case 'manual-set-course-completion':
      return `${
        messages.activitiesTextCompletionManualSet
      } ${getUser()} ${inCourse()}`;
    case 'manual-remove-course-completion':
      return `${
        messages.activitiesTextCompletionManualRemove
      } ${getUser()} ${inCourse()}`;
    case 'merge-users':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageMergeUsers,
        placeholders: {
          sourceUser: event.data?.source,
          targetUser: event.data?.target
        }
      });
    case 'pin-competency-profile':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessagePinCompetencyProfile,
        placeholders: {
          competencyProfile: event.competencyProfile?.title
            ? getCompetencyProfile()
            : getDeletedCompetencyProfile(),
          user: getUser()
        }
      });
    case 'publish-chapter':
      return `${published} ${
        messages.activitiesTextChapter
      } ${getChapter()}${inCourse()}`;
    case 'remove-achievement-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveAdminPermissionsGroup
            : messages.activitiesCommonRemoveAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inAchievement()
      );
    case 'remove-achievement-all-product-user-custom-attribute':
      return (
        messages.activitiesMessageRemoveProductUserCustomAttributeAll +
        inAchievement()
      );
    case 'remove-achievement-completions':
      return (
        (event.user
          ? getMessageWithPlaceholders({
              message: messages.activitiesMessageRemoveAchievementUser,
              placeholders: { user: getUser() }
            })
          : event.data?.groupCompletionIds
          ? getMessageWithPlaceholders({
              message:
                messages.activitiesMessageRemoveAchievementGroupCompletions,
              placeholders: {
                numberOfCompletions: event.data?.groupCompletionIds?.length
              }
            })
          : messages.activitiesMessageRemoveAchievementUsers) + inAchievement()
      );
    case 'remove-achievement-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveOwnerRightsGroup
            : messages.activitiesCommonRemoveOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inAchievement()
      );
    case 'remove-achievement-product-user-custom-attribute':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageRemoveProductUserCustomAttribute,
          placeholders: {
            productUserCustomAttribute: getDeletedProductUserCustomAttribute()
          }
        }) + inAchievement()
      );
    case 'remove-achievement-users-attachment':
      return (
        `${removed} ${messages.commonCertificate.toLowerCase()} ` +
        `${messages.activitiesTextFromUser} ${getUser()}` +
        `${inAchievement()}`
      );
    case 'remove-admin-group-user':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRemoveAdminGroupUser,
        placeholders: {
          adminGroup: getAdminGroup(),
          user: getUser()
        }
      });
    case 'remove-all-admin-group-users':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRemoveAllAdminGroupUsers,
        placeholders: {
          adminGroup: getAdminGroup()
        }
      });
    case 'remove-all-calendar-event-feedback-questions':
      return (
        getMessageWithPlaceholders({
          message:
            messages.activitiesMessageRemoveAllCalendarEventFeedbackQuestions,
          placeholders: {
            numberOfQuestions: event.data?.numberOfQuestions,
            user: ''
          }
        }) + fromCalendarEvent()
      );
    case 'remove-all-calendar-event-feedback-template-questions':
      return getMessageWithPlaceholders({
        message:
          messages.activitiesMessageRemoveAllCalendarEventFeedbackTemplateQuestions,
        placeholders: {
          numberOfQuestions: event.data?.numberOfQuestions,
          templateTitle: event.data?.templateTitle,
          user: ''
        }
      });
    case 'remove-all-supervisee':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRemoveSuperviseeAll,
        placeholders: { user: getUser() }
      });
    case 'remove-calendar-event-attachment':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageRemoveCalendarEventAttachment,
          placeholders: { numberOfFiles: event.data?.removedFiles?.length || 1 }
        }) + fromCalendarEvent()
      );
    case 'remove-calendar-event-feedback-question':
      return (
        getMessageWithPlaceholders({
          message:
            messages.activitiesMessageRemoveCalendarEventFeedbackQuestion,
          placeholders: { user: '' }
        }) + fromCalendarEvent()
      );
    case 'remove-calendar-event-feedback-submissions':
      return (
        getMessageWithPlaceholders({
          message:
            messages.activitiesMessageRemoveCalendarEventFeedbackSubmissions,
          placeholders: {
            numberOfSubmissions: event.data?.numberOfSubmissions,
            user: ''
          }
        }) + fromCalendarEvent()
      );
    case 'remove-calendar-event-feedback-template':
      // a global template, not scoped to a calendar event, so no
      // fromCalendarEvent() suffix
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRemoveCalendarEventFeedbackTemplate,
        placeholders: {
          templateTitle: event.data?.templateTitle,
          user: ''
        }
      });
    case 'remove-calendar-event-feedback-template-question':
      return getMessageWithPlaceholders({
        message:
          messages.activitiesMessageRemoveCalendarEventFeedbackTemplateQuestion,
        placeholders: {
          templateTitle: event.data?.templateTitle,
          user: ''
        }
      });
    case 'remove-calendar-event-all-product-user-custom-attribute':
      return (
        messages.activitiesMessageRemoveProductUserCustomAttributeAll +
        inCalendarEvent()
      );
    case 'remove-calendar-event-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveOwnerRightsGroup
            : messages.activitiesCommonRemoveOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCalendarEvent()
      );
    case 'remove-calendar-event-participation':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageRemoveCalendarEventParticipation,
          placeholders: {
            numberOfUsers: event.data?.userIds?.length || 1
          }
        }) + inCalendarEvent()
      );
    case 'remove-calendar-event-product-user-custom-attribute':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageRemoveProductUserCustomAttribute,
          placeholders: {
            productUserCustomAttribute: getDeletedProductUserCustomAttribute()
          }
        }) + inCalendarEvent()
      );
    case 'remove-calendar-event-self':
      return (
        messages.activitiesMessageRemoveCalendarEventSelf + inCalendarEvent()
      );
    case 'remove-calendar-event-users':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageRemoveCalendarEventUsers,
          placeholders: {
            numberOfUsers: event.data?.removedUserIds?.length || null
          }
        }) + fromCalendarEvent()
      );
    case 'remove-client-page-section-attachment':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRemoveClientPageSectionAttachment,
        placeholders: {
          attachmentName: getDeletedClientPageSectionAttachment(),
          sectionTitle: getClientPageSection()
        }
      });
    case 'remove-competency-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveAdminPermissionsGroup
            : messages.activitiesCommonRemoveAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCompetency()
      );
    case 'remove-competency-profile-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveAdminPermissionsGroup
            : messages.activitiesCommonRemoveAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCompetencyProfile()
      );
    case 'remove-course-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveAdminPermissionsGroup
            : messages.activitiesCommonRemoveAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCourse()
      );
    case 'remove-course-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveOwnerRightsGroup
            : messages.activitiesCommonRemoveOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCourse()
      );
    case 'remove-course-users':
      return (
        `${removed} ${
          event.user ? getUser() : messages.activitiesTextAllUsers
        }` + ` ${messages.activitiesTextFromCourse}${course()}`
      );
    case 'remove-email-message-template-product':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRemoveEmailMessageTemplateProduct,
        placeholders: {
          resourceTitle: event.data.productTitle,
          templateTitle: getEmailMessageTemplate(),
          user: ''
        }
      });
    case 'remove-group-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveAdminPermissionsGroup
            : messages.activitiesCommonRemoveAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inGroup()
      );
    case 'remove-group-course':
      return `${removed} ${
        messages.activitiesTextCourse
      } ${getCourse()}${fromGroup()}`;
    case 'remove-group-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveOwnerRightsGroup
            : messages.activitiesCommonRemoveOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inGroup()
      );
    case 'remove-group-from-supervisor':
      return getMessageWithPlaceholders({
        message: event.group
          ? messages.activitiesMessageRemoveGroupFromSupervisor
          : messages.activitiesMessageRemoveGroupFromSupervisorAll,
        placeholders: {
          groupTitle: getGroup(),
          user: getUser()
        }
      });
    case 'remove-group-user':
      return `${removed} ${
        messages.activitiesTextUser
      } ${getUser()} ${fromGroup()}`;

    case 'remove-path-group':
      return `${removed} ${
        messages.activitiesTextGroup
      } ${getGroup()}${fromPath()}`;
    case 'remove-path-owner':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonRemoveOwnerRightsGroup
            : messages.activitiesCommonRemoveOwnerRights,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inPath()
      );
    case 'remove-path-step':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageRemovePathStep,
          placeholders: { stepNumber: event.data?.stepNumber }
        }) + fromPath()
      );
    case 'remove-path-users':
      return (
        `${removed}` +
        (event.user
          ? ` ${messages.activitiesTextUser} ${getUser()}`
          : ` ${messages.activitiesTextAllUsers}`) +
        ` ${messages.activitiesTextFromPath} ${getPath()}`
      );
    case 'remove-supervisee':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRemoveSupervisee,
        placeholders: { user: getUser() }
      });
    case 'remove-user-competency':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRemoveUserCompetency,
        placeholders: {
          removedCompetency: event.data?.removedCompetency,
          user: getUser()
        }
      });
    case 'request-individual-achievement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageRequestIndividualAchievement,
        placeholders: {
          individualAchievement: getIndividualAchievement()
        }
      });
    case 'retry-failed-course-completion':
      return `${messages.activitiesTextRetriedFailedCompletion}` + inCourse();
    case 'revert-chapter-version':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageRevertChapterVersion,
          placeholders: {
            chapter: event.data.chapter.title,
            chapterVersion: getChapterVersion()
          }
        }) + inCourse()
      );
    case 'revert-course-version':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageRevertCourseVersion,
          placeholders: {
            courseVersion: getCourseVersion()
          }
        }) + inCourse()
      );
    case 'save-chapter-changes':
      return `${savedChanges} ${getChapter()}${inCourse()}`;
    case 'set-new-password':
      return messages.activitiesSetNewPassword;
    case 'set-new-password-admin':
      return `${set} ${messages.activitiesTextNewPasswordForUser} ${getUser()}`;
    case 'start-background-worker':
      return event.data?.running
        ? messages.activitiesMessageStartBackgroundWorker
        : messages.activitiesMessageStartBackgroundWorkerFailed;
    case 'start-new-course-completion':
      return `${messages.activitiesTextStartedNewCompletion}` + inCourse();
    case 'send-course-group-invites':
      return (
        `${sent} ` +
        (event.data && event.data.sentEmails
          ? `${event.data.sentEmails} `
          : '') +
        `${messages.activitiesTextCourseInviteEmails}${inCourse()}`
      );
    case 'send-group-invites':
      return (
        `${sent} ` +
        (event.data && event.data.sentEmails
          ? `${event.data.sentEmails} `
          : '') +
        `${messages.activitiesTextGroupInviteEmails}${inGroup()}`
      );
    case 'send-path-group-invites':
      return (
        `${sent} ` +
        (event.data && event.data.sentEmails
          ? `${event.data.sentEmails} `
          : '') +
        `${messages.activitiesTextPathInviteEmails}${inPath()}`
      );
    case 'send-path-user-invites':
      return (
        `${sent} ` +
        (event.data && event.data.sentEmails
          ? `${event.data.sentEmails} `
          : '') +
        `${messages.activitiesTextPathInviteEmails}${inPath()}`
      );
    case 'send-message':
      return (
        `${sent} ${messages.activitiesTextMessage}` +
        (event.data && event.data.successfulSends
          ? ` ${event.data.successfulSends}`
          : '') +
        ` ${messages.activitiesTextToUsers}.`
      );
    case 'submit-calendar-event-feedback':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageSubmitCalendarEventFeedback,
          placeholders: { user: '' }
        }) + inCalendarEvent()
      );
    case 'submit-course-completion-integration':
      return (
        messages.activitiesTextSubmittedCourseCompletion +
        (event.data?.targetSystem
          ? ` ${messages.activitiesTextToSystem} ${event.data?.targetSystem}`
          : '')
      );
    case 'transfer-admin-rights':
      return `${messages.activitiesTextTransferAdminRights} ${getUser()}`;
    case 'unlink-achievement-from-calendar-event':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageUnlinkAchievementFromCalendarEvent,
          placeholders: {
            achievementTitle: getAchievement()
          }
        }) + fromCalendarEvent()
      );
    case 'unlink-all-achievements-from-calendar-event':
      return (
        messages.activitiesMessageUnlinkAllAchievementsFromCalendarEvent +
        fromCalendarEvent()
      );
    case 'unpin-competency-profile':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUnpinCompetencyProfile,
        placeholders: {
          competencyProfile: event.competencyProfile?.title
            ? getCompetencyProfile()
            : getDeletedCompetencyProfile(),
          user: getUser()
        }
      });
    case 'unpublish-chapter':
      return `${unpublished} ${
        messages.activitiesTextChapter
      } ${getChapter()}${inCourse()}`;
    case 'update-achievement':
      return messages.activitiesMessageUpdateAchievement + inAchievement();
    case 'update-achievement-access-settings':
      return (
        messages.activitiesCommonUpdatedAccessAndVisibilitySettings +
        inAchievement()
      );
    case 'update-achievement-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonUpdateAdminPermissionsGroup
            : messages.activitiesCommonUpdateAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inAchievement()
      );
    case 'update-achievement-completions':
      return `${updated} ${messages.activitiesTextUser} ${getUserFromData()}
        ${inAchievement()}`;
    case 'update-achievement-order-product-user-custom-attribute':
      return (
        messages.activitiesMessageUpdateProductUserCustomAttributeOrder +
        inAchievement()
      );
    case 'update-achievement-product-user-custom-attribute':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageUpdateProductUserCustomAttribute,
          placeholders: {
            productUserCustomAttribute: getProductUserCustomAttribute()
          }
        }) + inAchievement()
      );
    case 'update-achievement-template':
      return (
        (event.data?.type === 'conversation'
          ? messages.activitiesMessageUpdateAchievementTemplateQuestions
          : messages.activitiesMessageUpdateAchievementTemplateTaskList) +
        inAchievement()
      );
    case 'update-achievement-completion-template':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageUpdateAchievementUserTemplate,
          placeholders: {
            user: getUser()
          }
        }) + inAchievement()
      );
    case 'update-admin-group':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateAdminGroup,
        placeholders: {
          adminGroup: getAdminGroup()
        }
      });
    case 'update-answer':
      return `${updated} ${messages.activitiesTextAnswer}${inCourse()}`;
    case 'update-answer-review':
      return `${updated} ${messages.activitiesTextAnswerReview}${inCourse()}`;
    case 'update-auth-strategy':
      return `updated auth strategy ${event.data?.identifier}`;
    case 'update-api-routes':
      return 'updated external API routes';
    case 'update-calendar-event':
      return messages.activitiesMessageUpdateCalendarEvent + inCalendarEvent();
    case 'update-calendar-event-access-settings':
      return (
        messages.activitiesCommonUpdatedAccessAndVisibilitySettings +
        inCalendarEvent()
      );
    case 'update-calendar-event-feedback-question':
      return (
        getMessageWithPlaceholders({
          message:
            messages.activitiesMessageUpdateCalendarEventFeedbackQuestion,
          placeholders: { user: '' }
        }) + inCalendarEvent()
      );
    case 'update-calendar-event-feedback-questions-order':
      return (
        getMessageWithPlaceholders({
          message:
            messages.activitiesMessageUpdateCalendarEventFeedbackQuestionsOrder,
          placeholders: { user: '' }
        }) + inCalendarEvent()
      );
    case 'update-calendar-event-feedback-template':
      // a global template, not scoped to a calendar event, so no
      // inCalendarEvent() suffix
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateCalendarEventFeedbackTemplate,
        placeholders: {
          templateTitle: event.data?.templateTitle,
          user: ''
        }
      });
    case 'update-calendar-event-feedback-template-question':
      return getMessageWithPlaceholders({
        message:
          messages.activitiesMessageUpdateCalendarEventFeedbackTemplateQuestion,
        placeholders: {
          templateTitle: event.data?.templateTitle,
          user: ''
        }
      });
    case 'update-calendar-event-feedback-template-questions-order':
      return getMessageWithPlaceholders({
        message:
          messages.activitiesMessageUpdateCalendarEventFeedbackTemplateQuestionsOrder,
        placeholders: {
          templateTitle: event.data?.templateTitle,
          user: ''
        }
      });
    case 'update-calendar-event-order-product-user-custom-attribute':
      return (
        messages.activitiesMessageUpdateProductUserCustomAttributeOrder +
        inCalendarEvent()
      );
    case 'update-calendar-event-product-user-custom-attribute':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageUpdateProductUserCustomAttribute,
          placeholders: {
            productUserCustomAttribute: getProductUserCustomAttribute()
          }
        }) + inCalendarEvent()
      );
    case 'update-chapter-slug':
      return `${updated} ${
        messages.activitiesTextLink
      } ${inChapter()}${inCourse()}`;
    case 'update-chapters-order':
      return `${updated} ${messages.activitiesTextChaptersOrder}${inCourse()}`;
    case 'update-client-page-section':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateClientPageSection,
        placeholders: { sectionTitle: getClientPageSection() }
      });
    case 'update-competency':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateCompetency,
        placeholders: { competency: getCompetency() }
      });
    case 'update-competency-access-settings':
      return (
        messages.activitiesCommonUpdatedAccessAndVisibilitySettings +
        inCompetency()
      );
    case 'update-competency-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonUpdateAdminPermissionsGroup
            : messages.activitiesCommonUpdateAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCompetency()
      );
    case 'update-competency-profile-admin':
      return (
        getMessageWithPlaceholders({
          message: event.adminGroup
            ? messages.activitiesCommonUpdateAdminPermissionsGroup
            : messages.activitiesCommonUpdateAdminPermissions,
          placeholders: {
            adminGroup: getAdminGroup(),
            user: getUser()
          }
        }) + inCompetencyProfile()
      );
    case 'update-competency-endorsement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateCompetencyEndorsement,
        placeholders: {
          user: getUser(),
          competency: getCompetency()
        }
      });
    case 'update-competency-profile':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateCompetencyProfile,
        placeholders: { competencyProfile: getCompetencyProfile() }
      });
    case 'update-competency-profile-access-settings':
      return (
        messages.activitiesCommonUpdatedAccessAndVisibilitySettings +
        inCompetencyProfile()
      );
    case 'update-course':
      return `${updated} ${messages.activitiesTextCourseSettings}${inCourse()}`;
    case 'update-course-admin':
      return `${updated} ${messages.activitiesTextCourseAdmin}${inCourse()}`;
    case 'update-course-language-version':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageUpdateCourseLanguageVersion,
          placeholders: {
            language: event.data?.language,
            user: ''
          }
        }) + inCourse()
      );
    case 'update-course-owner':
      return `${updated} ${messages.usersCourseOwner.toLowerCase()}${inCourse()}`;
    case 'update-course-publish':
      return `${updated} ${
        messages.activitiesTextCoursePublishSettings
      }${inCourse()}`;
    case 'update-course-user-settings':
      return `${updated} ${
        messages.activitiesTextPathCourseSettingsFor
      } ${getUser()}`;
    case 'update-course-user-settings-for-group':
      return `${updated} ${
        messages.activitiesTextPathCourseSettingsFor
      } ${inGroup()}`;
    case 'update-discussion-comment':
      return `${updated} ${
        messages.activitiesTextDiscussionComment
      }${inCourse()}`;
    case 'update-email-message-template':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateEmailMessageTemplate,
        placeholders: {
          templateTitle: getEmailMessageTemplate(),
          user: ''
        }
      });
    case 'update-email-message-template-product':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateEmailMessageTemplateProduct,
        placeholders: {
          resourceTitle: event.data.productTitle,
          templateTitle: getEmailMessageTemplate(),
          user: ''
        }
      });
    case 'update-group':
      return `${updated} ${messages.activitiesTextGroupSettings}${inGroup()}`;
    case 'update-group-admin':
      return (
        `${updated} ${messages.activitiesTextGroupAdmin}` +
        ` ${getUser()}${inGroup()}`
      );
    case 'update-individual-achievement':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateIndividualAchievement,
        placeholders: { achievement: getIndividualAchievement() }
      });
    case 'update-integration':
      return `updated integration configuration for ${getIntegrationIdentifier()}`;
    case 'update-password':
      return `${changed} ${messages.activitiesTextPassword}`;
    case 'update-path':
      return `${updated} ${messages.activitiesTextPathSettings}${inPath()}`;
    case 'update-path-step':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageUpdatePathStep,
          placeholders: { stepNumber: getPathStep() }
        }) + inPath()
      );
    case 'update-path-step-user-settings':
      return (
        getMessageWithPlaceholders({
          message: messages.activitiesMessageUpdatePathStepUserSettings,
          placeholders: { stepNumber: getPathStep(), user: getUser() }
        }) + inPath()
      );
    case 'update-path-steps-order':
      return messages.activitiesMessageUpdatePathStepsOrder + inPath();
    case 'update-profile':
      return `${updated} ${messages.activitiesTextProfile}`;
    case 'update-scorm-status':
      return `${updated} ${messages.activitiesTextScormStatus}`;
    case 'update-site-settings':
      return `${updated} ${messages.activitiesTextSiteSettings}`;
    case 'update-supervisor':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateSupervisor,
        placeholders: { user: getUser() }
      });
    case 'update-user':
      return `${updated} ${messages.activitiesTextUser} ${getUser()}`;
    case 'update-user-ambitions':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateUserAmbitions,
        placeholders: { user: getUser() }
      });
    case 'update-user-competencies':
      return getMessageWithPlaceholders({
        message: messages.activitiesMessageUpdateUserCompetencies,
        placeholders: { user: getUser() }
      });
    case 'achievement-completion-conversation-closed':
      return (
        getMessageWithPlaceholders({
          message:
            messages.activitiesMessageAchievementCompletionConversationClosed,
          placeholders: {
            user: getUserFromData()
          }
        }) + inAchievement()
      );
    case 'achievement-completion-closing-approval':
      return (
        getMessageWithPlaceholders({
          message:
            messages.activitiesMessageAchievementCompletionClosingApproval,
          placeholders: {
            targetUser:
              event.data?.targetUser?.email ||
              event.data?.targetUser?.username ||
              messages.activitiesTextUnknownUser
          }
        }) + inAchievement()
      );
    default: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _exhaustive: never = event.type; // If this errors, a newly added EventLogType is missing a case here
      return 'unknown event';
    }
  }
};

export default getEventMessage;
